<template>
  <div
    class="
      tw-rounded-lg
      tw-border
      tw-border-solid
      tw-border-sf-neutral-lighter-1
      tw-bg-white
    "
    :data-testid="`column-row-${column.uid}`"
    :data-column-type="column.type"
  >
    <!-- No left padding and no row gap: the drag handle brings its own
         fixed-width gutter, so anything on top of it reads as a gap twice. -->
    <div class="tw-flex tw-h-12 tw-items-center tw-pr-2">
      <SfDragHandle
        always-visible
        :dragging="false"
        data-testid="column-drag-handle"
      />
      <!-- A spacer has nothing to edit, so its title is text, not a control. -->
      <span
        v-if="isSpacer"
        :class="titleRowClasses"
        data-testid="column-row-toggle"
      >
        <span :class="titleClasses" data-testid="column-row-title">
          {{ title }}
        </span>
        <span :class="badgeClasses" data-testid="column-type-badge">
          {{ typeLabel }}
        </span>
      </span>

      <button
        v-else
        type="button"
        :class="[titleRowClasses, 'tw-appearance-none']"
        :aria-expanded="expanded"
        :aria-controls="formId"
        data-testid="column-row-toggle"
        @click="toggle"
      >
        <span :class="titleClasses" data-testid="column-row-title">
          {{ title }}
        </span>
        <span :class="badgeClasses" data-testid="column-type-badge">
          {{ typeLabel }}
        </span>
        <i
          class="fa tw-ml-2 tw-text-xs tw-text-sf-neutral-light-2"
          :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"
        />
      </button>

      <SfButtonDs
        icon-only
        icon-before="trash"
        variant="tertiary"
        size="sm"
        class="tw-ml-2 tw-shrink-0"
        :aria-label="t('remove')"
        data-testid="column-remove"
        @click="emit('remove')"
      />
    </div>

    <div
      v-if="expanded && !isSpacer"
      :id="formId"
      class="
        tw-border-x-0
        tw-border-b-0
        tw-border-t
        tw-border-solid
        tw-border-sf-neutral-lighter-1
        tw-p-3
        tw-text-xs
        tw-text-sf-neutral-dark-1
      "
      data-testid="column-form"
    >
      <ColumnDifferenceForm
        v-if="isDifference"
        :column="column"
        :fields="fields"
        :availability="availability"
        @update="onFieldUpdate"
      />

      <!-- Period / analytical: 2-column grid of dropdowns. -->
      <div v-else class="tw-grid tw-grid-cols-2 tw-items-start tw-gap-2">
        <ColumnFieldGroup
          :fields="fields"
          :values="column.values"
          :availability="availability"
          @update="onFieldUpdate"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { SfButtonDs } from '@silverfin/ui-components'
import SfDragHandle from '@app/shared/vue/components/sf-drag-handle/sf-drag-handle.vue'

import type { ColumnType, EditorColumn, TypeSchema } from '../types'
import { buildColumnTitle } from '../column-presentation'
import { fieldAvailability } from '../column-constraints'
import { useColumnEditorI18n } from '../use-column-editor-i18n'
import ColumnDifferenceForm from './column-difference-form.vue'
import ColumnFieldGroup from './column-field-group.vue'

const props =
  defineProps<{
    column: EditorColumn
    // The schema for this column's type. Undefined when the response carries a
    // column type it ships no schema for; the row then has nothing to expand.
    typeSchema?: TypeSchema
  }>()

const emit =
  defineEmits<{
    remove: []
    update: [key: string, value: string]
    // Opened to be read, which is not the same as opened and then edited. Only
    // the opening is worth relaying — collapsing a row again says nothing.
    expand: []
  }>()

const t = useColumnEditorI18n()

// Every field group relays to the same place, whichever part of the form it
// sits in.
const onFieldUpdate = (key: string, value: string) => emit('update', key, value)

const expanded = ref(false)

function toggle(): void {
  expanded.value = !expanded.value

  if (expanded.value) emit('expand')
}

// Ties the toggle to the panel it opens. The uid is unique across the drawer.
const formId = computed(() => `column-form-${props.column.uid}`)

const fields = computed(() => props.typeSchema?.fields ?? [])
const isSpacer = computed(() => props.column.type === 'ColumnSpacer')

// Some values rule others out — actuals plus budget is a whole-year figure, so
// it settles the year and period fields; adjustments only exist year-to-date.
// Computed for the column as a whole because a field in one operand of a
// difference can narrow one in the other.
const availability = computed(() =>
  fieldAvailability(props.column, props.typeSchema)
)

// A difference column compares two values (B − A) and gets its own form.
// Period/analytical columns lay out as a 2-column grid so the fields pair up
// (relative + show, then periods + delta).
const isDifference = computed(() => props.column.type === 'PeriodDifference')

const title = computed(() =>
  buildColumnTitle(
    props.column,
    props.typeSchema,
    t('column_types.empty_space')
  )
)

// `tw-p-0`: with no Tailwind preflight a raw <button> keeps the browser's
// default padding, which would indent an editable title past a spacer's.
const titleRowClasses =
  'tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2 tw-border-0 tw-p-0 tw-bg-transparent tw-text-left'

const titleClasses =
  'tw-truncate tw-text-sm tw-font-semibold tw-text-sf-neutral-darker-1'

// Each column type reads as its own kind of thing at a glance, so the badge
// carries a palette rather than a shape. Analytical has no colour in the design
// and takes the one family the other three leave free.
const TYPE_BADGES: Record<ColumnType, { key: string; classes: string }> = {
  Period: {
    key: 'period',
    classes:
      'tw-border-sf-info-lighter-1 tw-bg-sf-info-subtle-1 tw-text-sf-info-dark-1',
  },
  PeriodDifference: {
    key: 'difference',
    classes:
      'tw-border-sf-warning-lighter-1 tw-bg-sf-warning-subtle-1 tw-text-sf-warning-dark-1',
  },
  PeriodAnalytical: {
    key: 'analytical',
    classes:
      'tw-border-sf-success-lighter-1 tw-bg-sf-success-subtle-1 tw-text-sf-success-dark-1',
  },
  ColumnSpacer: {
    key: 'space',
    classes:
      'tw-border-sf-neutral-lighter-1 tw-bg-sf-neutral-subtle-1 tw-text-sf-neutral-dark-1',
  },
}

const typeLabel = computed(() =>
  t(`column_types.${TYPE_BADGES[props.column.type].key}`)
)

const badgeClasses = computed(() => [
  'tw-ml-auto tw-shrink-0 tw-whitespace-nowrap tw-rounded-full tw-border tw-border-solid tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium',
  TYPE_BADGES[props.column.type].classes,
])
</script>
